import api from './api';

export interface PaginatedResponse<T> {
  success: boolean;
  data: {
    items?: T[];
    products?: T[];
    users?: T[];
    orders?: T[];
    categories?: T[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
  message?: string;
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  code?: string;
}

export interface BaseEntity {
  id: string | number;
  createdAt?: string;
  updatedAt?: string;
}

export abstract class AbstractService<T extends BaseEntity> {
  protected basePath: string;

  constructor(basePath: string) {
    this.basePath = basePath;
  }

  /**
   * Get all entities with optional query parameters
   */
  async getAll(params?: URLSearchParams): Promise<PaginatedResponse<T>> {
    const queryString = params ? `?${params.toString()}` : '';
    const response = await api.get<PaginatedResponse<T>>(
      `${this.basePath}${queryString}`
    );
    return response.data;
  }

  /**
   * Get a single entity by ID
   */
  async getOne(id: string | number): Promise<ApiResponse<T>> {
    const response = await api.get<ApiResponse<T>>(`${this.basePath}/${id}`);
    return response.data;
  }

  /**
   * Create a new entity
   */
  async create(data: Partial<T>): Promise<ApiResponse<T>> {
    const response = await api.post<ApiResponse<T>>(this.basePath, data);
    return response.data;
  }

  /**
   * Update an existing entity
   */
  async update(
    id: string | number,
    data: Partial<T>
  ): Promise<ApiResponse<T>> {
    const response = await api.put<ApiResponse<T>>(
      `${this.basePath}/${id}`,
      data
    );
    return response.data;
  }

  /**
   * Delete an entity
   */
  async remove(id: string | number): Promise<ApiResponse<void>> {
    const response = await api.delete<ApiResponse<void>>(
      `${this.basePath}/${id}`
    );
    return response.data;
  }

  /**
   * Search entities
   */
  async search(
    query: string,
    params?: URLSearchParams
  ): Promise<PaginatedResponse<T>> {
    const searchParams = params || new URLSearchParams();
    searchParams.set('search', query);
    return this.getAll(searchParams);
  }
}
